// Dedicated Coaching Store - Phase 3: State Management Optimization
import { create } from 'zustand';
import { persist, subscribeWithSelector, devtools } from 'zustand/middleware';
import type { CoachingInsights, TrainingLoad, ChatMessage } from '@/types/business';

interface CoachingPreferences {
  units: 'metric' | 'imperial';
  goals: string[];
  focusAreas: string[];
  aiPersonality: 'professional' | 'friendly' | 'motivational';
}

interface CoachingState {
  insights: {
    running: CoachingInsights | null;
    fitness: CoachingInsights | null;
  };
  chatHistory: ChatMessage[];
  preferences: CoachingPreferences;
  trainingLoad?: TrainingLoad;
}

interface CoachingActions {
  setInsights: (type: 'running' | 'fitness', insights: CoachingInsights) => void;
  addChatMessage: (message: ChatMessage) => void;
  clearChatHistory: () => void;
  setPreferences: (preferences: Partial<CoachingPreferences>) => void;
  setTrainingLoad: (load: TrainingLoad) => void;
  clearInsights: () => void;
  isInsightsStale: () => boolean;
}

type CoachingStore = CoachingState & CoachingActions;

export const useCoachingStore = create<CoachingStore>()(
  devtools(
    subscribeWithSelector(
      persist(
        (set, get) => ({
          // Initial state
          insights: { running: null, fitness: null },
          chatHistory: [],
          preferences: {
            units: 'metric',
            goals: [],
            focusAreas: [],
            aiPersonality: 'friendly',
          },
          trainingLoad: undefined,

          // Actions
          setInsights: (type, insights) => {
            set((state) => ({
              insights: {
                ...state.insights,
                [type]: { ...insights, lastUpdated: Date.now() },
              },
            }));
          },

          addChatMessage: (message) => {
            set((state) => ({
              chatHistory: [...state.chatHistory, message].slice(-50), // Keep last 50
            }));
          },

          clearChatHistory: () => {
            set({ chatHistory: [] });
          },

          setPreferences: (preferences) => {
            set((state) => ({
              preferences: {
                ...state.preferences,
                ...preferences,
              },
            }));
          },

          setTrainingLoad: (load) => {
            set({ trainingLoad: load });
          },

          clearInsights: () => {
            set({
              insights: { running: null, fitness: null },
              trainingLoad: undefined,
            });
          },

          isInsightsStale: () => {
            // Simplified for now
            return false;
          },
        }),
        {
          name: 'coaching-storage',
          partialize: (state) => ({
            preferences: state.preferences,
          }),
        }
      )
    ),
    { name: 'Coaching Store' }
  )
);

// Optimized selectors
export const useCoachingInsights = () => useCoachingStore((state) => state.insights);
export const useRunningInsights = () => useCoachingStore((state) => state.insights.running);
export const useFitnessInsights = () => useCoachingStore((state) => state.insights.fitness);
export const useChatHistory = () => useCoachingStore((state) => state.chatHistory);
export const useCoachingPreferences = () => useCoachingStore((state) => state.preferences);
export const useTrainingLoad = () => useCoachingStore((state) => state.trainingLoad);